import {
  type FunctionComponent,
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
} from "react";

const ANNOUNCEMENT_LAYOUT_DURATION_MS = 220;

interface LayoutAnimation {
  animation: Animation;
  translation: number;
}

/**
 * 一覧・広告を告知の取得で待たせず、バナーの伸縮による移動だけをFLIPで補正する。
 * ResizeObserverで描画前に補正し、表示済みの要素をtransformで新しい位置へ移す。
 */
export const DesktopAnnouncementContent: FunctionComponent<
  PropsWithChildren
> = ({ children }: PropsWithChildren) => {
  const containerRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const banner = container.querySelector<HTMLElement>(
      ".desktop-catalog-banner",
    );
    let previousPositions = new Map<HTMLElement, number>();
    let previousWidth = container.offsetWidth;
    let previousBannerHeight = banner?.offsetHeight ?? 0;
    const animations = new Map<HTMLElement, LayoutAnimation>();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const measure = (): void => {
      const width = container.offsetWidth;
      const bannerHeight = banner?.offsetHeight ?? 0;
      const currentPositions = new Map<HTMLElement, number>();
      const translations = new Map<HTMLElement, number>();
      const targets = container.querySelectorAll<HTMLElement>(
        ":scope > *, :scope > .desktop-catalog-banner > *",
      );
      for (const target of targets) {
        const top = target.offsetTop;
        currentPositions.set(target, top);
        const previousTop = previousPositions.get(target);
        if (
          previousTop === undefined ||
          previousTop === top ||
          bannerHeight === previousBannerHeight ||
          reducedMotion.matches ||
          width !== previousWidth ||
          typeof target.animate !== "function"
        )
          continue;
        const active = animations.get(target);
        const progress =
          active?.animation.effect?.getComputedTiming().progress ?? 1;
        const remaining = (active?.translation ?? 0) * (1 - progress);
        translations.set(target, previousTop - top + remaining);
      }
      for (const [target, active] of animations) {
        if (
          !currentPositions.has(target) ||
          reducedMotion.matches ||
          width !== previousWidth
        ) {
          active.animation.cancel();
          animations.delete(target);
        }
      }
      for (const [target, translation] of translations) {
        animations.get(target)?.animation.cancel();
        const animation = target.animate(
          [
            { transform: `translateY(${translation}px)` },
            { transform: "translateY(0)" },
          ],
          { duration: ANNOUNCEMENT_LAYOUT_DURATION_MS, easing: "ease-out" },
        );
        animations.set(target, { animation, translation });
      }
      previousPositions = currentPositions;
      previousWidth = width;
      previousBannerHeight = bannerHeight;
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    if (banner) observer.observe(banner);
    reducedMotion.addEventListener("change", measure);
    return (): void => {
      observer.disconnect();
      reducedMotion.removeEventListener("change", measure);
      for (const { animation } of animations.values()) animation.cancel();
    };
  }, []);

  return (
    <section ref={containerRef} aria-label="お知らせと一覧">
      {children}
    </section>
  );
};
